import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Search, Edit, Trash2, Eye } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';

const PodcastList = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  useEffect(() => {
    loadEpisodes();
  }, []);

  const loadEpisodes = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/admin/articles?content_type=podcast');
      setArticles(response.data.articles || []);
    } catch (error) {
      console.error('Failed to load podcast episodes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (articleId) => {
    try {
      await api.delete(`/api/admin/articles/${articleId}`);
      setArticles(articles.filter((a) => a.id !== articleId));
      setDeleteConfirm(null);
      toast.success('Podcast episode deleted successfully');
    } catch (error) {
      console.error('Failed to delete podcast episode:', error);
      toast.error('Failed to delete podcast episode. Please try again.');
    }
  };

  const filteredArticles = articles.filter((article) =>
    (article.title || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (article.summary || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (article.category || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading podcast episodes...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="podcast-list">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Podcast Episodes</h1>
          <p className="text-gray-600">Manage Student Signal podcast episodes</p>
        </div>
        <Link
          to="/admin/podcast/new"
          className="flex items-center gap-2 px-6 py-3 text-white rounded-xl transition-colors shadow-lg"
          style={{ backgroundColor: '#1a5d3a' }}
          data-testid="add-podcast-btn"
        >
          <Plus size={20} />
          <span className="font-medium">Add New Episode</span>
        </Link>
      </div>

      {/* Search */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
          <input
            type="text"
            placeholder="Search podcast episodes by title, category, or summary..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-xl transition-all outline-none"
            data-testid="podcast-search-input"
          />
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-6 py-4 text-sm font-semibold text-gray-900">Episode</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-gray-900">Category</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-gray-900">Status</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-gray-900">Published</th>
                <th className="text-right px-6 py-4 text-sm font-semibold text-gray-900">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredArticles.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-gray-500">
                    No podcast episodes found.{' '}
                    <Link
                      to="/admin/podcast/new"
                      style={{ color: '#1a5d3a' }}
                      className="font-medium underline"
                    >
                      Create the first one
                    </Link>
                    .
                  </td>
                </tr>
              ) : (
                filteredArticles.map((article) => (
                  <tr key={article.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-start gap-3">
                        {article.main_image_url && (
                          <img
                            src={article.main_image_url}
                            alt={article.title}
                            className="w-16 h-16 rounded-lg object-cover"
                          />
                        )}
                        <div>
                          <div className="font-medium text-gray-900 line-clamp-1">{article.title}</div>
                          <div className="text-sm text-gray-500 line-clamp-1">{article.summary}</div>
                          {article.is_featured && (
                            <span
                              className="text-xs px-2 py-0.5 rounded mt-1 inline-block"
                              style={{ backgroundColor: '#FEF3C7', color: '#92400E' }}
                            >
                              Featured
                            </span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className="inline-block px-3 py-1 rounded-full text-xs font-medium"
                        style={{ backgroundColor: '#F0FDF4', color: '#1a5d3a' }}
                      >
                        {article.category}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${
                          article.is_published ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                        }`}
                      >
                        {article.is_published ? 'Published' : 'Draft'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-gray-700 text-sm">
                      {article.published_at
                        ? new Date(article.published_at).toLocaleDateString()
                        : 'N/A'}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-2">
                        {article.is_published && (
                          <Link
                            to={`/podcast/${article.slug}`}
                            className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="View"
                          >
                            <Eye size={18} />
                          </Link>
                        )}
                        <Link
                          to={`/admin/podcast/${article.id}/edit`}
                          className="p-2 text-gray-600 hover:bg-green-50 rounded-lg transition-colors"
                          style={{ color: '#1a5d3a' }}
                          title="Edit"
                          data-testid={`edit-podcast-${article.id}`}
                        >
                          <Edit size={18} />
                        </Link>
                        <button
                          onClick={() => setDeleteConfirm(article.id)}
                          className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete"
                          data-testid={`delete-podcast-${article.id}`}
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Delete Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-2">Confirm Delete</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete this podcast episode? This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="flex-1 px-4 py-2 border-2 border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors font-medium"
                data-testid="cancel-delete-btn"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm)}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors font-medium"
                data-testid="confirm-delete-btn"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PodcastList;
